import RouteRequest from "../models/RouteRequest";
import RouteRequestPoint from "../models/RouteRequestPoint";
import geoStorages from "../utils/SessionStorageRingBuffer";
import api from "../api";

/** @param geoPoint {GeoPoint} */
export const formatGeoPoint = (geoPoint) => {
  return geoPoint ? `${geoPoint.lat.toFixed(6)}, ${geoPoint.lon.toFixed(6)}` : undefined;
}

/**
 * Создает маршрут из объекта параметров QueryString
 * @param keyPoints
 * @param oldRoute { RouteRequest}
 * @param routePartName
 * @returns { RouteRequest}
 */
export const createRouteRequestFromKeyPoints = async (keyPoints, oldRoute, routePartName) => {
  
  const cityIds = keyPoints.map(point => point.cityId);
  const geoPoints = keyPoints.filter(point => !point.cityId).map(point => point.geoPoint); // координаты точек, у которых нет cityId. Для них будем делать обратное гео-кодирование.
  let geoCodedObjects = [];
  
  if (geoPoints && geoPoints.length > 0) {
    /** @type {GeoCodingResult[]} */
    geoCodedObjects = await api.geo.reverseGeocoding(geoPoints);
    geoCodedObjects.forEach(o => cityIds.push(o.cityId));
  }

  /** @type {City[]} */
  let cities = [];
  if (cityIds && cityIds.length > 0) {
    cities = await geoStorages.cities.getByIds(cityIds);
  }
  
  /** @type {Map<number, City>} */
  const citiesMap = new Map(cities.map(c => [c.id, c]));
  /** @type {Map<GeoPoint, GeoCodingResult>} */
  const geoCodedObjectsMap = new Map(geoCodedObjects.map(o => [o.geoPoint, o]));

  const routeRequestPointsPromises = keyPoints.map(async keyPoint => {
    const city = citiesMap.get(keyPoint.cityId);
    const geoCodedObject = geoCodedObjectsMap.get(keyPoint.geoPoint);
    if (!city && !geoCodedObject) {
      return undefined;
    }

    const routePoint = createDefaultRouteRequestPoint();
    if (geoCodedObject) {
      await fillRouteRequestPointFromGeoCodedObject(routePoint, geoCodedObject);
    } else {
      fillRouteRequestPointFromCity(routePoint, city);
      if (!keyPoint.isCenterCityPoint) {
        routePoint.geoPoint = keyPoint.geoPoint; // Пользователь передавал координаты вместе с cityId. Они приоритетнее, чем координаты центра города.
      }
    }

    routePoint.isStartPoint = keyPoint.isStartPoint;
    routePoint.isEndPoint = keyPoint.isEndPoint;
    routePoint.isIntermediatePoint = keyPoint.isIntermediatePoint;
    routePoint.isLoadingPoint = keyPoint.isLoadingPoint;
    routePoint.isUnloadingPoint = keyPoint.isUnloadingPoint;

    return routePoint;
  }).filter(Boolean);
  const routeRequestPoints = await Promise.all(routeRequestPointsPromises);

  if (routePartName === "from") {
    return new RouteRequest({
      ...oldRoute,
      from: {
        ...routeRequestPoints[0],
      }
    });
  }
  if (routePartName === "to") {
    return new RouteRequest({
      ...oldRoute,
      to: {
        ...routeRequestPoints[0],
      }
    });
  }
  if (routePartName === "through") {
    const through = [...routeRequestPoints];
    const from = through.shift();
    const to = through.pop();
    return new RouteRequest({
      from,
      to,
      through
    });
  }

  return oldRoute;
};

/** @returns { RouteRequestPoint} */
export const createDefaultRouteRequestFromPoint = () => {
  const routeRequestPoint = createDefaultRouteRequestPoint();
  routeRequestPoint.isStartPoint = true;
  return routeRequestPoint;
};

/** @returns { RouteRequestPoint} */
export const createDefaultRouteRequestToPoint = () => {
  const routeRequestPoint = createDefaultRouteRequestPoint();
  routeRequestPoint.isEndPoint = true;
  return routeRequestPoint;
};

/** @returns { RouteRequestPoint} */
export const createDefaultRouteRequestThroughPoint = () => {
  const routeRequestPoint = createDefaultRouteRequestPoint();
  routeRequestPoint.isIntermediatePoint = true;
  return routeRequestPoint;
};

/** @returns { RouteRequestPoint} */
export const createDefaultRouteRequestPoint = () => {
  return new RouteRequestPoint();
};

/**
 * заполняет адресные свойства точки маршрута из объекта города
 * @param routeRequestPoint { RouteRequestPoint}
 * @param city { City}
 */
export const fillRouteRequestPointFromCity = (routeRequestPoint, city) => {
  routeRequestPoint.country = city.country;
  routeRequestPoint.region = city.region;
  routeRequestPoint.city = city;
  routeRequestPoint.geoPoint = city.geoPoint;
  routeRequestPoint.isCenterCityPoint = true;
};

/**
 * заполняет адресные свойства точки маршрута из объекта гео-кодирования
 * @param routeRequestPoint { RouteRequestPoint}
 * @param {GeoCodingResult} geoCodedObject
 */
export const fillRouteRequestPointFromGeoCodedObject = async (routeRequestPoint, geoCodedObject) => {
  routeRequestPoint.country = geoCodedObject.countryId ? (await geoStorages.countries.getByIds([geoCodedObject.countryId]))[0] : undefined;
  routeRequestPoint.region = geoCodedObject.regionId ? (await geoStorages.regions.getByIds([geoCodedObject.regionId]))[0] : undefined;
  routeRequestPoint.city = geoCodedObject.cityId ? (await geoStorages.cities.getByIds([geoCodedObject.cityId]))[0] : undefined;
  routeRequestPoint.geoPoint = geoCodedObject.geoPoint;
  routeRequestPoint.isCenterCityPoint = false;
};

export const defaultRouteRequest = Object.freeze(
  new RouteRequest({from: createDefaultRouteRequestFromPoint(), to: createDefaultRouteRequestToPoint(), through: []}));

/**
 * @param routePoint
 */
export const routeRequestPointIsSet = (routePoint) => {
  return routePoint.cityId || routePoint.geoPoint;
};
